/**
 * The active region.
 *
 * Region is not a permission and is not enforced here. The API has already
 * filtered the schema response to what the caller may read, so a user with one
 * region's grants receives one region and never sees this switcher. All this
 * holds is *which* of the regions they can see they are currently looking at —
 * a view preference, in the same class as a sort order.
 *
 * It is deliberately not in the URL. The config route already carries the
 * region inside its key (`/config/anz-mailboxes`), so putting it in the path as
 * well would give two sources of truth for the same fact and an obvious way for
 * them to disagree.
 */

import { createContext, useContext } from 'react'

export const REGION_STORAGE_KEY = 'email-config-ui:region'

export interface RegionState {
  /** Active region key, or '' before the schema has loaded. */
  region: string
  setRegion: (key: string) => void
}

const RegionContext = createContext<RegionState>({ region: '', setRegion: () => {} })

export const RegionProvider = RegionContext.Provider

export function useRegion(): RegionState {
  return useContext(RegionContext)
}

/** Last region this browser looked at. Absent, blocked or corrupt all mean "no preference". */
export function readStoredRegion(): string | null {
  try {
    return window.localStorage.getItem(REGION_STORAGE_KEY)
  } catch {
    return null
  }
}

export function writeStoredRegion(key: string) {
  try {
    window.localStorage.setItem(REGION_STORAGE_KEY, key)
  } catch {
    // Private window, blocked site data, or a browser that simply says no.
    // A forgotten preference is not worth a broken screen.
  }
}
